import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { AuthService, CmsService, Language, LanguageService, SiteContext, SiteContextConfig } from '@spartacus/core';
import { CookieService } from 'ngx-cookie';
import { BehaviorSubject, Observable, combineLatest, zip } from 'rxjs';
import { mergeMap, take, tap } from 'rxjs/operators';
import { getContextParameterDefault, getContextParameterValues } from 'src/app/core/site-context/context-config-utils';
import { B2bUserService } from '../b2buser/b2b-user.service';
import { ShimanoBusinessTypeService } from '../businessType/shimano-business-type.service';
import { ComponentService } from '../component.service';
import { ShimanoCountryService } from '../country/shimano-country.service';
import { SiteContextService } from '../site-context/site-context.service';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class ShimanoLanguageService implements SiteContext<Language> {
  private activeLanguage: BehaviorSubject<string>;

  constructor(
    private config: SiteContextConfig,
    private languageService: LanguageService,
    private cookieService: CookieService,
    private authService: AuthService,
    private b2bUserService: B2bUserService,
    private cmsService: CmsService,
    private location: Location,
    private siteContextService: SiteContextService,
    private shimanoBusinessTypeService: ShimanoBusinessTypeService,
    private shimanoCountryService: ShimanoCountryService,
    private storageService: StorageService,
    private componentService: ComponentService
  ) {
    this.activeLanguage = new BehaviorSubject<string>(this.initLanguage);
  }

  getActive(): Observable<string> {
    return this.activeLanguage;
  }

  getAll(): Observable<Language[]> {
    return this.languageService.getAll();
  }

  setActive(language: string) {
    combineLatest([this.shimanoBusinessTypeService.getActive(), this.shimanoCountryService.getActive()])
      .pipe(
        mergeMap(([businessType, country]) =>
          zip(
            this.authService.isUserLoggedIn(),
            this.b2bUserService.getB2bUserProfile(),
            this.siteContextService.getLanguages$(businessType, country)
          )
        ),
        take(1),
        tap(([isUserLoggedIn, user, availableLanguages]: [boolean, any, string[]]) => {
          this.componentService.sethasFreightLevel(user?.freightLevel);
          const selectedLanguageOnLogin = this.storageService.getLocalStorageItem('selectedLanguageOnLogin');
          let userLanguage = selectedLanguageOnLogin ? selectedLanguageOnLogin : language;
          if (isUserLoggedIn && user && user.language && !selectedLanguageOnLogin) {
            userLanguage = user.language.isocode;
          } else {
            userLanguage = availableLanguages.includes(language) ? language : availableLanguages[0];
          }
          this.activeLanguage.next(userLanguage);
          this.languageService.setActive(userLanguage);
          this.cookieService.put('userLanguage', userLanguage);
          this.storageService.setSessionStorageItem('language', userLanguage);
          this.cmsService.refreshLatestPage();
        })
      )
      .subscribe();
  }

  get initLanguage(): string {
    const languageInUrl = this.location.path().split('/')[3];
    return languageInUrl && getContextParameterValues(this.config, 'shimanoLanguage').includes(languageInUrl)
      ? languageInUrl
      : getContextParameterDefault(this.config, 'shimanoLanguage');
  }
}
